import { CommonModule } from '@angular/common';
import { Component, inject, signal, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { forkJoin } from 'rxjs';
import { NgIcon } from '@ng-icons/core';
import { ButtonDirective } from '../../components/button/button.directive';
import { InputComponent } from '../../components/input/input.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { SelectComponent } from '../../components/select/select.component';
import { FilterOption } from '../../components/table/table.interface';
import {
  CompanyNotificationUser,
  CreateNotificationPayload,
  NotificationType,
} from '../../interfaces/apis/notifications';
import { DictionaryService } from '../../services/dictionary.service';
import { NotificationsService } from '../../services/notifications.service';
import { SnackbarService } from '../../services/snackbar.service';
import { UserRole } from '../../interfaces/user-role';
import { isRole } from '../../utils/user-roles';

@Component({
  selector: 'app-notifications',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    ButtonDirective,
    LoadingSpinnerComponent,
    ModalComponent,
    NgIcon,
  ],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent {
  @ViewChild('applicationSelect') applicationSelect?: SelectComponent;
  @ViewChild('typeSelect') typeSelect?: SelectComponent;
  @ViewChild('recipientsSelect') recipientsSelect?: SelectComponent;

  protected readonly dictionaryService = inject(DictionaryService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly snackbarService = inject(SnackbarService);

  protected readonly loading = signal(true);
  protected readonly loadError = signal<string | null>(null);
  protected readonly submitting = signal(false);
  protected readonly helpModalOpened = signal(false);
  protected readonly applications = signal<string[]>([]);
  protected readonly notificationTypes = signal<NotificationType[]>([]);
  protected readonly recipientOptions = signal<string[]>([]);
  protected readonly recipientIds = signal<string[]>([]);

  // Validation display state (following create-draft pattern)
  protected readonly hasSubmitted = signal(false);
  protected readonly touchedSelects = signal<Record<string, boolean>>({});

  private users: CompanyNotificationUser[] = [];

  protected readonly notificationForm = new FormGroup({
    title: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    body: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    application: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    notificationType: new FormControl<NotificationType | ''>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    recipientIds: new FormControl<string[]>([], {
      nonNullable: true,
    }),
  });

  ngOnInit(): void {
    this.loadPageData();
  }

  protected get f() {
    return this.notificationForm.controls;
  }

  protected loadPageData(): void {
    this.loading.set(true);
    this.loadError.set(null);

    forkJoin({
      metadata: this.notificationsService.getMetadata(),
      users: this.notificationsService.getCompanyUsers(),
    }).subscribe({
      next: ({ metadata, users }) => {
        this.applications.set(metadata.data.applications || []);
        this.notificationTypes.set(metadata.data.notificationTypes || []);
        this.users = (users.data.users || []).filter(
          (user) => user.isActive !== false && user.isDeleted !== true,
        );
      },
      error: () => {
        this.loadError.set(
          this.dictionaryService.get('NotificationsLoadError') ||
            'Failed to load notification data',
        );
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  protected onApplicationChange(option: FilterOption): void {
    this.f.application.patchValue(option.value);
    this.f.application.markAsTouched();
  }

  protected onNotificationTypeChange(option: FilterOption): void {
    const notificationType = option.value as NotificationType;

    this.f.notificationType.patchValue(notificationType);
    this.f.notificationType.markAsTouched();
    this.clearRecipients();
    this.updateRecipientOptions(notificationType);
  }

  protected onRecipientsChange(ids: string[]): void {
    this.f.recipientIds.patchValue(ids);
    this.f.recipientIds.markAsTouched();
  }

  protected recipientsRequired(): boolean {
    const notificationType = this.f.notificationType.value;
    return notificationType === 'Team' || notificationType === 'Individuals';
  }

  protected recipientLabel(): string {
    if (this.f.notificationType.value === 'Team') {
      return this.dictionaryService.get('Supervisors') || 'Supervisors';
    }

    return this.dictionaryService.get('Users') || 'Users';
  }

  protected recipientPlaceholder(): string {
    if (this.f.notificationType.value === 'Team') {
      return (
        this.dictionaryService.get('SelectSupervisors') || 'Select supervisors'
      );
    }

    return this.dictionaryService.get('SelectUsers') || 'Select users';
  }

  protected hasRecipientError(): boolean {
    return (
      this.recipientsRequired() &&
      this.f.recipientIds.value.length === 0 &&
      (this.hasSubmitted() ||
        !!this.touchedSelects()['recipientIds'] ||
        this.f.recipientIds.dirty ||
        this.f.recipientIds.touched)
    );
  }

  /**
   * Marks a select field as touched on focusout (following create-draft pattern).
   * Ignores focus moves within the same select element.
   */
  protected markSelectTouched(field: string, event: FocusEvent): void {
    const nextFocusedElement = event.relatedTarget as Node | null;
    const selectElement = event.currentTarget as HTMLElement | null;

    if (nextFocusedElement && selectElement?.contains(nextFocusedElement)) {
      return;
    }

    setTimeout(() => {
      this.touchedSelects.update((fields) => ({
        ...fields,
        [field]: true,
      }));
    });
  }

  /**
   * Checks if a required select is in an invalid state (following create-draft pattern).
   */
  protected isRequiredSelectInvalid(value: string, field: string): boolean {
    return (this.hasSubmitted() || !!this.touchedSelects()[field]) && !value;
  }

  protected canSubmit(): boolean {
    const formValue = this.notificationForm.getRawValue();

    return (
      formValue.title.trim().length > 0 &&
      formValue.body.trim().length > 0 &&
      formValue.application.length > 0 &&
      formValue.notificationType.length > 0 &&
      (!this.recipientsRequired() || formValue.recipientIds.length > 0)
    );
  }

  protected submit(): void {
    this.hasSubmitted.set(true);

    if (!this.canSubmit()) {
      this.notificationForm.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload();
    this.submitting.set(true);
    this.notificationsService.createNotification(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackbarService.showSuccess(
            this.dictionaryService.get('NotificationCreatedSuccess') ||
              'Notification created successfully',
          );
          this.resetForm();
        } else {
          this.snackbarService.showError(
            this.dictionaryService.get('NotificationCreateFailed') ||
              response.message ||
              'Failed to create notification',
          );
        }
      },
      error: () => {
        this.snackbarService.showError(
          this.dictionaryService.get('NetworkError') ||
            'Network error, please try again',
        );
        this.submitting.set(false);
      },
      complete: () => {
        this.submitting.set(false);
      },
    });
  }

  protected reset(): void {
    this.resetForm();
  }

  protected openHelpModal(): void {
    this.helpModalOpened.set(true);
  }

  private buildPayload(): CreateNotificationPayload {
    const formValue = this.notificationForm.getRawValue();
    const basePayload = {
      title: formValue.title.trim(),
      body: formValue.body.trim(),
      application: formValue.application,
    };

    if (formValue.notificationType === 'All') {
      return {
        ...basePayload,
        notificationType: 'All',
      };
    }

    if (formValue.notificationType === 'Team') {
      return {
        ...basePayload,
        notificationType: 'Team',
        supervisorIds: formValue.recipientIds,
      };
    }

    return {
      ...basePayload,
      notificationType: 'Individuals',
      receiversIds: formValue.recipientIds,
    };
  }

  private clearRecipients(): void {
    this.f.recipientIds.patchValue([]);
    this.f.recipientIds.markAsPristine();
    this.f.recipientIds.markAsUntouched();
    this.touchedSelects.update(({ recipientIds, ...fields }) => fields);
    this.recipientsSelect?.checkOption('', 'none', false);
  }

  private updateRecipientOptions(notificationType: NotificationType): void {
    if (notificationType === 'All') {
      this.recipientOptions.set([]);
      this.recipientIds.set([]);
      return;
    }

    const users =
      notificationType === 'Team'
        ? this.users.filter((user) => isRole(user.role, UserRole.SUPERVISOR))
        : this.users;

    this.recipientOptions.set(users.map((user) => this.getUserLabel(user)));
    this.recipientIds.set(users.map((user) => user.id));
  }

  private resetForm(): void {
    this.notificationForm.reset({
      title: '',
      body: '',
      application: '',
      notificationType: '',
      recipientIds: [],
    });

    this.hasSubmitted.set(false);
    this.touchedSelects.set({});
    this.recipientOptions.set([]);
    this.recipientIds.set([]);
    this.applicationSelect?.updateSelectedOption('');
    this.typeSelect?.updateSelectedOption('');
    this.recipientsSelect?.checkOption('', 'none', false);
  }

  private getUserLabel(user: CompanyNotificationUser): string {
    return user.name?.trim() || user.username?.trim() || user.email?.trim() || user.id;
  }
}
